import React, { useState, useEffect, useRef, memo } from 'react';
import { TrendingUp, Download, Clock, BarChart3, TrendingDown } from 'lucide-react';

// TradingView Widget Component
const TradingViewWidget = ({ symbol = "BINANCE:BTCUSDT" }) => {
  const containerRef = useRef(null);

  useEffect(() => {
    if (!containerRef.current) return;

    containerRef.current.innerHTML = ''; // clear old widget

    const script = document.createElement('script');
    script.src = 'https://s3.tradingview.com/external-embedding/embed-widget-advanced-chart.js';
    script.type = 'text/javascript';
    script.async = true;
    script.innerHTML = JSON.stringify({
      autosize: true,
      symbol: symbol.startsWith('BINANCE:') ? symbol : `BINANCE:${symbol}`,
      interval: "1",
      timezone: "Etc/UTC",
      theme: "dark",
      style: "1",
      locale: "en",
      enable_publishing: false,
      allow_symbol_change: true,
      calendar: false,
      support_host: "https://www.tradingview.com"
    });

    const wrapper = document.createElement('div');
    wrapper.className = 'tradingview-widget-container';
    const widgetDiv = document.createElement('div');
    widgetDiv.className = 'tradingview-widget-container__widget';
    wrapper.appendChild(widgetDiv);
    wrapper.appendChild(script);
    containerRef.current.appendChild(wrapper);
  }, [symbol]);

  return <div ref={containerRef} style={{ width: '100%', height: '100%' }} />;
};

// Heatmap Component
const TimeHeatmap = ({ data, title, color, type }) => {
  const maxValue = Math.max(...data);
  
  const getIntensity = (value) => {
    if (maxValue === 0) return 0;
    return value / maxValue;
  };

  const getHourLabel = (hour) => {
    return `${hour.toString().padStart(2, '0')}:00`;
  };

  return (
    <div style={{ width: '100%' }}>
      <h4 style={{ 
        fontSize: '14px', 
        fontWeight: '600', 
        marginBottom: '16px',
        color: 'var(--text-primary)'
      }}>
        {title}
      </h4>
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(12, 1fr)',
        gap: '2px',
        marginBottom: '12px'
      }}>
        {data.slice(0, 12).map((value, index) => (
          <div
            key={index}
            style={{
              aspectRatio: '1',
              backgroundColor: `${color}${Math.round(getIntensity(value) * 255).toString(16).padStart(2, '0')}`,
              border: '1px solid var(--border-color)',
              borderRadius: '4px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '10px',
              color: getIntensity(value) > 0.5 ? '#000' : 'var(--text-secondary)',
              position: 'relative',
              cursor: 'pointer'
            }}
            title={`${getHourLabel(index)}: ${value} occurrences`}
          >
            {index}
          </div>
        ))}
      </div>
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(12, 1fr)',
        gap: '2px'
      }}>
        {data.slice(12, 24).map((value, index) => (
          <div
            key={index + 12}
            style={{
              aspectRatio: '1',
              backgroundColor: `${color}${Math.round(getIntensity(value) * 255).toString(16).padStart(2, '0')}`,
              border: '1px solid var(--border-color)',
              borderRadius: '4px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '10px',
              color: getIntensity(value) > 0.5 ? '#000' : 'var(--text-secondary)',
              position: 'relative',
              cursor: 'pointer'
            }}
            title={`${getHourLabel(index + 12)}: ${value} occurrences`}
          >
            {index + 12}
          </div>
        ))}
      </div>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginTop: '12px',
        fontSize: '11px',
        color: 'var(--text-secondary)'
      }}>
        <span>Less frequent</span>
        <div style={{ display: 'flex', gap: '2px' }}>
          {[0.2, 0.4, 0.6, 0.8, 1.0].map(intensity => (
            <div
              key={intensity}
              style={{
                width: '12px',
                height: '12px',
                backgroundColor: `${color}${Math.round(intensity * 255).toString(16).padStart(2, '0')}`,
                border: '1px solid var(--border-color)',
                borderRadius: '2px'
              }}
            />
          ))}
        </div>
        <span>More frequent</span>
      </div>
    </div>
  );
};

// Statistics Card Component
const StatCard = ({ title, value, subtitle, icon: Icon, color }) => (
  <div style={{
    background: 'var(--tertiary-dark)',
    border: '1px solid var(--border-color)',
    borderRadius: 'var(--border-radius)',
    padding: '20px'
  }}>
    <div style={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '16px'
    }}>
      <h4 style={{ 
        fontSize: '12px', 
        color: 'var(--text-secondary)', 
        textTransform: 'uppercase',
        fontWeight: '600'
      }}>
        {title}
      </h4>
      {Icon && <Icon size={16} style={{ color }} />}
    </div>
    <div style={{ 
      fontSize: '24px', 
      fontWeight: '700', 
      color: 'var(--text-primary)', 
      marginBottom: '8px' 
    }}>
      {value}
    </div>
    <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
      {subtitle}
    </div>
  </div>
);

const PriceAnalysis = ({ priceAnalyzer, showToast }) => {
  const [selectedSymbol, setSelectedSymbol] = useState('');
  const [selectedDays, setSelectedDays] = useState(14);
  const [selectedTimezone, setSelectedTimezone] = useState(8);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [filteredSymbols, setFilteredSymbols] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [analysisResults, setAnalysisResults] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (selectedSymbol) {
      const filtered = priceAnalyzer.symbols?.filter(s => 
        s.symbol.toLowerCase().includes(selectedSymbol.toLowerCase()) ||
        s.baseAsset.toLowerCase().includes(selectedSymbol.toLowerCase())
      ).slice(0, 20) || [];
      setFilteredSymbols(filtered);
    } else {
      // Show popular symbols by default
      const popular = ['BTCUSDT', 'ETHUSDT', 'BNBUSDT', 'SOLUSDT', 'ADAUSDT', 'XRPUSDT', 'DOGEUSDT', 'MATICUSDT'];
      const filtered = popular
        .map(symbol => priceAnalyzer.symbols?.find(s => s.symbol === symbol))
        .filter(Boolean) || [];
      setFilteredSymbols(filtered);
    }
  }, [selectedSymbol, priceAnalyzer.symbols]);

  const handleAnalysis = async () => {
    if (!selectedSymbol.trim()) {
      showToast('error', 'Invalid Input', 'Please enter a trading pair');
      return;
    }

    const finalSymbol = selectedSymbol.endsWith('USDT') ? selectedSymbol : selectedSymbol + 'USDT';
    const symbolExists = priceAnalyzer.symbols?.find(s => s.symbol === finalSymbol.toUpperCase());
    
    if (!symbolExists && priceAnalyzer.symbols?.length > 0) {
      showToast('error', 'Symbol Not Found', `Trading pair ${finalSymbol} not found`);
      return;
    }

    setIsAnalyzing(true);
    try {
      const results = await priceAnalyzer.performAnalysis(finalSymbol.toUpperCase(), selectedDays, selectedTimezone);
      setAnalysisResults(results);
      setShowResults(true);
      showToast('success', 'Analysis Complete', `Analysis completed for ${finalSymbol}`);
    } catch (error) {
      showToast('error', 'Analysis Failed', error.message);
    } finally {
      setIsAnalyzing(false);
    }
  };

  const selectSymbol = (symbol) => {
    setSelectedSymbol(symbol);
    setShowDropdown(false);
  };

  // Generate mock data for demonstration if no analysis results
  const displayData = analysisResults || {
    stats: {
      totalDays: selectedDays,
      avgHighTime: '15:30',
      avgLowTime: '08:45',
      priceRange: { highest: 42500, lowest: 40200 },
      mostFrequentHighHour: { hour: 15, count: 8, percentage: 57 },
      mostFrequentLowHour: { hour: 8, count: 6, percentage: 43 }
    },
    highDistribution: [2, 3, 1, 0, 1, 2, 4, 3, 2, 1, 3, 4, 5, 6, 4, 8, 6, 5, 7, 4, 3, 2, 1, 2],
    lowDistribution: [3, 4, 2, 1, 2, 4, 5, 6, 8, 4, 3, 2, 1, 2, 3, 2, 1, 2, 3, 4, 2, 1, 2, 3]
  };

  return (
    <div className="page-section">
      <div className="control-panel">
        <div className="panel-title">
          <TrendingUp size={16} />
          Price Analysis Configuration
        </div>
        
        {/* Mobile-responsive controls grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : '2fr 1fr 1fr 120px',
          gap: isMobile ? '16px' : '20px',
          alignItems: 'end'
        }}>
          <div className="form-group">
            <label className="form-label">Trading Pair</label>
            <div className="symbol-input-container" style={{ position: 'relative' }}>
              <input
                type="text"
                className="form-input"
                placeholder="Search symbols..."
                value={selectedSymbol}
                onChange={(e) => setSelectedSymbol(e.target.value)}
                onFocus={() => setShowDropdown(true)}
                onBlur={() => setTimeout(() => setShowDropdown(false), 200)}
              />
              {showDropdown && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  background: 'var(--tertiary-dark)',
                  border: '1px solid var(--border-color)',
                  borderTop: 'none',
                  borderRadius: '0 0 var(--border-radius) var(--border-radius)',
                  maxHeight: '200px',
                  overflowY: 'auto',
                  zIndex: 1000,
                  boxShadow: 'var(--shadow-md)'
                }}>
                  {filteredSymbols.map((symbol) => (
                    <div
                      key={symbol.symbol}
                      style={{
                        padding: '10px 12px',
                        cursor: 'pointer',
                        borderBottom: '1px solid var(--border-color)',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        transition: 'background-color var(--transition-fast)'
                      }}
                      onMouseEnter={(e) => e.target.style.background = 'var(--hover-bg)'}
                      onMouseLeave={(e) => e.target.style.background = 'transparent'}
                      onClick={() => selectSymbol(symbol.symbol)}
                    >
                      <span style={{ fontWeight: 500, color: 'var(--text-primary)' }}>
                        {symbol.symbol}
                      </span>
                      <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>
                        {symbol.baseAsset}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Time Period</label>
            <select 
              className="form-select"
              value={selectedDays}
              onChange={(e) => setSelectedDays(parseInt(e.target.value))}
            >
              <option value={3}>3 Days</option>
              <option value={7}>7 Days</option>
              <option value={14}>14 Days</option>
              <option value={30}>30 Days</option>
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Timezone</label>
            <select 
              className="form-select"
              value={selectedTimezone}
              onChange={(e) => setSelectedTimezone(parseInt(e.target.value))}
            >
              <option value={8}>UTC+8 (Taipei)</option>
              <option value={0}>UTC+0 (London)</option>
              <option value={-5}>UTC-5 (New York)</option>
              <option value={9}>UTC+9 (Tokyo)</option>
            </select>
          </div>
          <div className="form-group">
            <button 
              className="btn primary"
              onClick={handleAnalysis}
              disabled={isAnalyzing}
              style={{ 
                height: 'fit-content',
                width: isMobile ? '100%' : 'auto'
              }}
            >
              {isAnalyzing ? (
                <>
                  <div className="loading-spinner"></div>
                  Analyzing...
                </>
              ) : (
                'Analyze'
              )}
            </button>
          </div>
        </div>
      </div>

      {showResults && (
        <div className="analysis-results">
          {/* TradingView Chart */}
          <div className="chart-panel" style={{ gridColumn: '1 / -1' }}>
            <div style={{
              position: 'relative',
              width: '100%',
              height: isMobile ? '300px' : '450px',
              background: 'var(--primary-dark)',
              borderRadius: 'var(--border-radius)',
              border: '1px solid var(--border-color)',
              overflow: 'hidden'
            }}>
              <TradingViewWidget symbol={selectedSymbol || 'BTCUSDT'} />
            </div>
          </div>

          {/* Statistics Overview - Mobile responsive grid */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '16px',
            marginTop: '24px'
          }}>
            <StatCard
              title="Analysis Period"
              value={`${displayData.stats.totalDays} days`}
              subtitle="Data points analyzed"
              icon={BarChart3}
              color="var(--accent-blue)"
            />
            <StatCard
              title="Avg High Time"
              value={displayData.stats.avgHighTime}
              subtitle={`UTC${selectedTimezone >= 0 ? '+' : ''}${selectedTimezone}`}
              icon={TrendingUp}
              color="var(--accent-green)"
            />
            <StatCard
              title="Avg Low Time"
              value={displayData.stats.avgLowTime}
              subtitle={`UTC${selectedTimezone >= 0 ? '+' : ''}${selectedTimezone}`}
              icon={TrendingDown}
              color="var(--accent-red)"
            />
            <StatCard
              title="Price Range"
              value={`$${(displayData.stats.priceRange.highest - displayData.stats.priceRange.lowest).toFixed(2)}`}
              subtitle={`H: $${displayData.stats.priceRange.highest.toFixed(2)} L: $${displayData.stats.priceRange.lowest.toFixed(2)}`}
              icon={Clock}
              color="var(--accent-gold)"
            />
          </div>

          {/* Heatmaps - Stack on mobile */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
            gap: '24px',
            marginTop: '24px'
          }}>
            <div className="chart-panel">
              <div className="chart-header">
                <div className="chart-title">
                  <div style={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    background: 'var(--accent-green)'
                  }}></div>
                  Daily High Time Distribution
                </div>
              </div>
              <div style={{ padding: isMobile ? '16px' : '20px' }}>
                <TimeHeatmap 
                  data={displayData.highDistribution}
                  title="Most frequent high times (24h format)"
                  color="#10b981"
                  type="high"
                />
                <div style={{
                  marginTop: '16px',
                  padding: '12px',
                  background: 'var(--primary-dark)',
                  borderRadius: '8px',
                  fontSize: '12px',
                  color: 'var(--text-secondary)'
                }}>
                  <strong>Peak Hour:</strong> {displayData.stats.mostFrequentHighHour.hour}:00 
                  ({displayData.stats.mostFrequentHighHour.count} times, {displayData.stats.mostFrequentHighHour.percentage}%)
                </div>
              </div>
            </div>

            <div className="chart-panel">
              <div className="chart-header">
                <div className="chart-title">
                  <div style={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    background: 'var(--accent-red)'
                  }}></div>
                  Daily Low Time Distribution
                </div>
              </div>
              <div style={{ padding: isMobile ? '16px' : '20px' }}>
                <TimeHeatmap 
                  data={displayData.lowDistribution}
                  title="Most frequent low times (24h format)"
                  color="#ef4444"
                  type="low"
                />
                <div style={{
                  marginTop: '16px',
                  padding: '12px',
                  background: 'var(--primary-dark)',
                  borderRadius: '8px',
                  fontSize: '12px',
                  color: 'var(--text-secondary)'
                }}>
                  <strong>Peak Hour:</strong> {displayData.stats.mostFrequentLowHour.hour}:00 
                  ({displayData.stats.mostFrequentLowHour.count} times, {displayData.stats.mostFrequentLowHour.percentage}%)
                </div>
              </div>
            </div>
          </div>

          {/* Additional Insights - Mobile responsive */}
          <div className="dashboard-card" style={{ marginTop: '24px' }}>
            <div className="card-header">
              <h3>Trading Insights</h3>
            </div>
            <div className="card-content">
              <div style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(300px, 1fr))',
                gap: '20px'
              }}>
                <div style={{
                  background: 'var(--tertiary-dark)',
                  border: '1px solid var(--border-color)',
                  borderRadius: 'var(--border-radius)',
                  padding: isMobile ? '16px' : '20px'
                }}>
                  <h4 style={{ color: 'var(--accent-green)', marginBottom: '12px' }}>High Price Patterns</h4>
                  <p style={{ fontSize: '14px', color: 'var(--text-secondary)', lineHeight: '1.5' }}>
                    Daily highs most frequently occur around <strong>{displayData.stats.mostFrequentHighHour.hour}:00</strong>, 
                    representing {displayData.stats.mostFrequentHighHour.percentage}% of observations. 
                    This suggests consistent market activity patterns during this timeframe.
                  </p>
                </div>
                
                <div style={{
                  background: 'var(--tertiary-dark)',
                  border: '1px solid var(--border-color)',
                  borderRadius: 'var(--border-radius)',
                  padding: isMobile ? '16px' : '20px'
                }}>
                  <h4 style={{ color: 'var(--accent-red)', marginBottom: '12px' }}>Low Price Patterns</h4>
                  <p style={{ fontSize: '14px', color: 'var(--text-secondary)', lineHeight: '1.5' }}>
                    Daily lows typically occur around <strong>{displayData.stats.mostFrequentLowHour.hour}:00</strong>, 
                    accounting for {displayData.stats.mostFrequentLowHour.percentage}% of cases. 
                    This pattern may indicate reduced trading volume or market sentiment shifts.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PriceAnalysis;